/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { SearchV2Filter, EstateV2Filter } from "~/generated/graphql";
import { DateRange, EstatePeriod, Granularity } from "~/components";
import { EstateGroupBy, EstateRecordsChart } from "./Charts";
import { EstateFiltersAndChartParams } from "./EstateFiltersAndChartParams";
import { mapEstatePeriodToBillingPeriod } from "./helpers";
import { FiltersProp } from "./types";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  granularity: Granularity;
  estateFilters: { filter: EstateV2Filter };
  restFilters: FiltersProp;
  disableDailyView: boolean;
  handleFilters: (filter: SearchV2Filter) => void;
  onPeriodChange: (
    selectedPeriod: EstatePeriod,
    selectedRange?: DateRange
  ) => void;
  onGranularityChange: (granularity: Granularity) => void;
  isCostLoading: boolean;
  currentMonthTotal: string | undefined;
  estateRecordsCount: number;
  monthlySavings: string;
  activeChart: EstateGroupBy;
};

export function EstateRecordsChartView({
  period,
  range,
  granularity,
  estateFilters,
  restFilters,
  disableDailyView,
  isCostLoading,
  currentMonthTotal,
  estateRecordsCount,
  monthlySavings,
  handleFilters,
  onPeriodChange,
  onGranularityChange,
  activeChart,
}: Props) {
  const handlePeriodChange = (
    selectedPeriod: EstatePeriod,
    selectedRange?: DateRange
  ) => {
    onPeriodChange(selectedPeriod, selectedRange);

    if (selectedPeriod !== EstatePeriod.RANGE) {
      const billingPeriod = mapEstatePeriodToBillingPeriod(selectedPeriod);

      handleFilters({
        billingPeriod,
      });
    }
  };

  return (
    <>
      <EstateFiltersAndChartParams
        period={period}
        range={range}
        granularity={granularity}
        restFilters={restFilters}
        disableDailyView={disableDailyView}
        handleFilters={handleFilters}
        activeChart={activeChart}
        onPeriodChange={handlePeriodChange}
        onGranularityChange={onGranularityChange}
      />
      <EstateRecordsChart
        granularity={granularity}
        period={period}
        activeChart={activeChart}
        estateFilters={estateFilters}
        range={range}
        isCostLoading={isCostLoading}
        currentMonthTotal={currentMonthTotal}
        estateRecordsCount={estateRecordsCount}
        monthlySavings={monthlySavings}
      />
    </>
  );
}
